'use client';

import { useEffect, useLayoutEffect, useState, type ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import {
  ActivitySkeleton,
  FloorSkeleton,
  ItemSkeleton,
  LensSkeleton,
  SectionSkeleton,
  TagsSkeleton,
  sectionOf,
  skeletonFor,
} from '@/app/skeletons';
import { Modal } from './modal';
import { PathTrail } from './path-trail';
import { LoadError } from './load-error';
// How long the tap's mount waits for the router to commit before it gives way
// to the error state: the same 10 s an act waits for its reply (docs/LOADING.md
// §5.6, §4.5).
import { REPLY_LIMIT_MS } from '@/lib/reach';
import {
  markTapLeft,
  pathnameOf,
  standsOver,
  useDismissTapMount,
  useTapTarget,
  useTapTargetHere,
  type TapTarget,
} from './tap-mount-store';
import { useT } from '@/lib/locale-context';
import styles from './tap-mount.module.css';

/**
 * The target's trail in the top bar while a skeleton stands, from the tap's
 * store: `target` on the tap's mount, the store itself on the route's mount
 * when the current route is its target. Draws nothing where the store holds
 * no trail (a direct visit, a refresh).
 */
export function TapMountTrail({ target }: { target?: TapTarget }) {
  const here = useTapTargetHere();
  const trail = (target ?? here)?.trail;
  return trail ? <PathTrail root={trail.root} items={trail.items} /> : null;
}

/**
 * The skeleton mounted by the tap (docs/LOADING.md §2, tool 3). Mounted once
 * in the shell, beside the peek slot; it draws the target's skeleton over the
 * content column while the tap's store names a target the pathname has not
 * reached. An item or a section opens as the peek in-app (the `@modal`
 * intercept catches every soft navigation to one), so those draw in the
 * peek's frame (`Modal`, marked `tapMount`); a list or any other page draws
 * in the content column's frame. When the router commits, the route's
 * `loading.tsx` draws the same skeleton with the same title, and this one
 * unmounts in that same commit.
 *
 * Esc and the dock's Back dismiss it (`useDismissTapMount`). After 10 s with
 * no commit its shapes give way to the error state in the same frame.
 */
export function TapMount() {
  const target = useTapTarget();
  const pathname = usePathname();
  const dismiss = useDismissTapMount();

  // Once the pathname leaves the page the tap was taken on (the target
  // arrived, or a nav lane was tapped over the skeleton), the tap's mount is
  // done there: coming back must not raise it again.
  useEffect(() => {
    if (target !== null && pathname !== target.from) markTapLeft();
  }, [target, pathname]);

  const standing = standsOver(target, pathname);

  // Esc dismisses, in the capture phase so the peek underneath (a drill from
  // one item to the next) never takes the same key as its own close. The
  // guards are the peek's own (modal.tsx): a native dialog or an open kit
  // Menu owns Esc while it is up. A layout effect, so the listener exists
  // from the commit that draws the skeleton, before paint (modal.tsx says
  // why a passive one is too late).
  useLayoutEffect(() => {
    if (!standing) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (document.querySelector('dialog[open]')) return;
      for (const menu of document.querySelectorAll('[role="menu"]')) {
        if (!menu.closest('dialog:not([open])')) return;
      }
      if (dismiss()) e.stopPropagation();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [standing, dismiss]);

  if (!standing || target === null) return null;
  // Keyed by the tap, so a second tap starts its own 10 s.
  return <TapMountFrame key={`${target.at}:${target.href}`} target={target} onBack={dismiss} />;
}

function TapMountFrame({ target, onBack }: { target: TapTarget; onBack: () => void }) {
  const path = pathnameOf(target.href);
  const kind = skeletonFor(path);
  const section = sectionOf(path);
  const failed = useCommitLimit(target.at);
  const body = failed ? (
    // Retry is a document load of the target: no cache and no pending
    // transition can hold that (docs/LOADING.md tool 10).
    <LoadError
      what={kind === 'item' || kind === 'section' ? 'item' : 'page'}
      offline={failed.offline}
      onRetry={() => window.location.assign(target.href)}
      onBack={onBack}
      testId="tap-mount-error"
    />
  ) : kind === 'item' ? (
    <ItemSkeleton target={target} />
  ) : section ? (
    <SectionSkeleton section={section} target={target} />
  ) : kind === 'lens' ? (
    <LensSkeleton />
  ) : kind === 'activity' ? (
    <ActivitySkeleton />
  ) : kind === 'tags' ? (
    <TagsSkeleton />
  ) : (
    <FloorSkeleton />
  );
  if (kind === 'item' || kind === 'section') {
    return <PeekFrame target={target}>{body}</PeekFrame>;
  }
  return (
    <div className={styles.frame} data-testid="tap-mount">
      {failed ? <div className={styles.pad}>{body}</div> : body}
    </div>
  );
}

/** The peek's frame for an item or a section target: the kit `Modal`, which
 *  fades in here, once, and flags the shell's peek chrome so the route's
 *  `Modal` mounts settled. */
function PeekFrame({ target, children }: { target: TapTarget; children: ReactNode }) {
  const t = useT();
  return (
    <Modal
      label={t('shell.loading.label')}
      fallbackHref="/work"
      tapMount
      toolbar={<TapMountTrail target={target} />}
    >
      {children}
    </Modal>
  );
}

/** Whether the tap at `at` has waited past the limit without a commit. */
function useCommitLimit(at: number): { offline: boolean } | null {
  const [failed, setFailed] = useState<{ offline: boolean } | null>(null);
  useEffect(() => {
    const timer = setTimeout(
      () => setFailed({ offline: typeof navigator !== 'undefined' && !navigator.onLine }),
      Math.max(0, at + REPLY_LIMIT_MS - Date.now()),
    );
    return () => clearTimeout(timer);
  }, [at]);
  return failed;
}
